/**
 * 前端加密工具类
 * 支持AES对称加密和RSA非对称加密
 * 与后端CryptoUtil.java兼容
 */

// 使用 ES6 import 方式导入（webpack 会在构建时处理）
import CryptoJS from 'crypto-js'
let JSEncrypt = null

// 尝试导入 jsencrypt（用于RSA加密）
try {
  // jsencrypt 的导入方式
  const jsencryptModule = require('jsencrypt')
  // jsencrypt 直接导出构造函数，格式：function JSEncrypt()
  // 也可能导出为对象：{ JSEncrypt: function }
  if (typeof jsencryptModule === 'function') {
    // 直接是构造函数
    JSEncrypt = jsencryptModule
  } else if (jsencryptModule && jsencryptModule.JSEncrypt && typeof jsencryptModule.JSEncrypt === 'function') {
    // 对象形式：{ JSEncrypt: function }
    JSEncrypt = jsencryptModule.JSEncrypt
  } else if (jsencryptModule && jsencryptModule.default && typeof jsencryptModule.default === 'function') {
    // ES6 默认导出
    JSEncrypt = jsencryptModule.default
  } else {
    throw new Error('JSEncrypt not found in module')
  }
  
  // 验证 JSEncrypt 是否是构造函数
  if (typeof JSEncrypt !== 'function') {
    throw new Error('JSEncrypt is not a constructor, type: ' + typeof JSEncrypt)
  }
} catch (e) {
  try {
    // 尝试从 window 对象获取
    if (typeof window !== 'undefined' && window.JSEncrypt) {
      JSEncrypt = window.JSEncrypt
    } else {
      throw new Error('JSEncrypt not available')
    }
  } catch (e2) {
    // jsencrypt 未安装，RSA加密功能不可用
  }
}

/**
 * 加密类型枚举
 */
export const CryptoType = {
  AES: 'AES',
  RSA: 'RSA'
}

/**
 * 默认AES密钥（应与后端保持一致）
 */
const DEFAULT_AES_KEY = '2nHtbGAYxq3wgZZdcFtye5LKYSuAmWVz'

/**
 * AES加密
 * @param {String} content - 待加密内容
 * @param {String} key - 密钥（如果为null，使用默认密钥）
 * @returns {String} 加密后的Base64字符串
 */
export function aesEncrypt(content, key = null) {
  if (!content) {
    return content
  }

  if (!CryptoJS) {
    throw new Error('crypto-js 未安装，无法使用AES加密')
  }

  try {
    const secretKey = key || DEFAULT_AES_KEY
    const keyUtf8 = CryptoJS.enc.Utf8.parse(secretKey)

    // 使用 AES/ECB/PKCS5Padding 加密（与后端兼容）
    const encrypted = CryptoJS.AES.encrypt(
      CryptoJS.enc.Utf8.parse(content),
      keyUtf8,
      {
        mode: CryptoJS.mode.ECB,
        padding: CryptoJS.pad.Pkcs7
      }
    )

    // 转换为 Base64 字符串
    let base64 = encrypted.ciphertext.toString(CryptoJS.enc.Base64)

    // 处理特殊字符，避免URL传输问题（与后端保持一致）
    base64 = base64.replace(/\//g, '~SLASH~').replace(/\+/g, '~SUB~')

    return base64
  } catch (error) {
    throw new Error('AES加密失败: ' + error.message)
  }
}

/**
 * AES解密
 * @param {String} content - 待解密内容
 * @param {String} key - 密钥（如果为null，使用默认密钥）
 * @returns {String} 解密后的字符串
 */
export function aesDecrypt(content, key = null) {
  if (!content) {
    return content
  }

  if (!CryptoJS) {
    throw new Error('crypto-js 未安装，无法使用AES解密')
  }

  try {
    const secretKey = key || DEFAULT_AES_KEY
    const keyUtf8 = CryptoJS.enc.Utf8.parse(secretKey)

    // 恢复特殊字符
    content = content.replace(/~SLASH~/g, '/').replace(/~SUB~/g, '+')

    // Base64解码
    const encryptedData = CryptoJS.enc.Base64.parse(content)

    // 创建加密对象
    const encrypted = CryptoJS.lib.CipherParams.create({
      ciphertext: encryptedData
    })

    // 解密
    const decrypted = CryptoJS.AES.decrypt(
      encrypted,
      keyUtf8,
      {
        mode: CryptoJS.mode.ECB,
        padding: CryptoJS.pad.Pkcs7
      }
    )

    // 转换为UTF-8字符串
    return decrypted.toString(CryptoJS.enc.Utf8)
  } catch (error) {
    throw new Error('AES解密失败: ' + error.message)
  }
}

/**
 * RSA加密（使用公钥）
 * @param {String} content - 待加密内容
 * @param {String} publicKey - 公钥（Base64编码）
 * @returns {String} 加密后的Base64字符串
 */
export function rsaEncrypt(content, publicKey) {
  if (!content || !publicKey) {
    return content
  }

  if (!JSEncrypt) {
    throw new Error('jsencrypt 未安装，无法使用RSA加密')
  }

  try {
    // JSEncrypt 本身就是构造函数
    const encrypt = new JSEncrypt()
    
    // 如果公钥不是完整的PEM格式，需要转换
    let formattedKey = publicKey
    if (!publicKey.includes('-----BEGIN PUBLIC KEY-----')) {
      formattedKey = `-----BEGIN PUBLIC KEY-----\n${publicKey}\n-----END PUBLIC KEY-----`
    }
    
    encrypt.setPublicKey(formattedKey)
    const encrypted = encrypt.encrypt(content)
    
    // RSA加密返回的是Base64字符串，可能包含URL不安全的字符（/和+）
    // 需要替换为安全字符，与后端保持一致
    if (encrypted) {
      return encrypted.replace(/\//g, '~SLASH~').replace(/\+/g, '~SUB~')
    }
    
    return encrypted
  } catch (error) {
    throw new Error('RSA加密失败: ' + error.message)
  }
}

/**
 * RSA解密（使用私钥）
 * @param {String} content - 待解密内容
 * @param {String} privateKey - 私钥（Base64编码）
 * @returns {String} 解密后的字符串
 */
export function rsaDecrypt(content, privateKey) {
  if (!content || !privateKey) {
    return content
  }

  if (!JSEncrypt) {
    throw new Error('jsencrypt 未安装，无法使用RSA解密')
  }

  try {
    // JSEncrypt 本身就是构造函数
    const encrypt = new JSEncrypt()
    
    // 如果私钥不是完整的PEM格式，需要转换
    let formattedKey = privateKey
    if (!privateKey.includes('-----BEGIN PRIVATE KEY-----') && !privateKey.includes('-----BEGIN RSA PRIVATE KEY-----')) {
      // 尝试两种格式
      formattedKey = `-----BEGIN PRIVATE KEY-----\n${privateKey}\n-----END PRIVATE KEY-----`
    }
    
    encrypt.setPrivateKey(formattedKey)
    
    const decrypted = encrypt.decrypt(content)
    
    // JSEncrypt.decrypt() 可能返回 false 或 null，需要检查
    if (decrypted === false || decrypted === null) {
      // 尝试使用 RSA PRIVATE KEY 格式
      if (!privateKey.includes('-----BEGIN RSA PRIVATE KEY-----')) {
        const rsaFormattedKey = `-----BEGIN RSA PRIVATE KEY-----\n${privateKey}\n-----END RSA PRIVATE KEY-----`
        encrypt.setPrivateKey(rsaFormattedKey)
        const retryDecrypted = encrypt.decrypt(content)
        if (retryDecrypted !== false && retryDecrypted !== null) {
          return String(retryDecrypted)
        }
      }
      throw new Error('RSA解密失败：解密结果为 ' + decrypted + '，可能是私钥配置不正确或与公钥不匹配')
    }
    
    // 确保返回字符串
    return String(decrypted)
  } catch (error) {
    throw new Error('RSA解密失败: ' + error.message)
  }
}

/**
 * 根据类型加密
 * @param {String} content - 待加密内容
 * @param {String} cryptoType - 加密类型：'AES' 或 'RSA'
 * @param {String} key - 密钥（AES密钥或RSA公钥）
 * @returns {String} 加密后的字符串
 */
export function encrypt(content, cryptoType, key) {
  if (cryptoType === CryptoType.AES) {
    return aesEncrypt(content, key)
  } else if (cryptoType === CryptoType.RSA) {
    return rsaEncrypt(content, key)
  } else {
    throw new Error('不支持的加密类型: ' + cryptoType)
  }
}

/**
 * 根据类型解密
 * @param {String} content - 待解密内容
 * @param {String} cryptoType - 加密类型：'AES' 或 'RSA'
 * @param {String} key - 密钥（AES密钥或RSA私钥）
 * @returns {String} 解密后的字符串
 */
export function decrypt(content, cryptoType, key) {
  if (cryptoType === CryptoType.AES) {
    return aesDecrypt(content, key)
  } else if (cryptoType === CryptoType.RSA) {
    return rsaDecrypt(content, key)
  } else {
    throw new Error('不支持的加密类型: ' + cryptoType)
  }
}

/**
 * 生成AES密钥（16字节，Base64编码）
 * @returns {String} Base64编码的AES密钥
 */
export function generateAESKey() {
  if (!CryptoJS) {
    throw new Error('crypto-js 未安装，无法生成AES密钥')
  }
  
  try {
    // 生成16字节（128位）的随机密钥
    const key = CryptoJS.lib.WordArray.random(16)
    // 转换为Base64字符串
    return CryptoJS.enc.Base64.stringify(key)
  } catch (error) {
    throw new Error('生成AES密钥失败: ' + error.message)
  }
}

/**
 * 混合加密：使用RSA公钥加密AES密钥，使用AES密钥加密数据
 * 数据格式：{"encryptedKey":"RSA加密的AES密钥","encryptedData":"AES加密的数据"}
 * 
 * @param {String} content - 待加密内容
 * @param {String} rsaPublicKey - RSA公钥（Base64编码）
 * @returns {String} JSON格式的加密数据：{"encryptedKey":"...","encryptedData":"..."}
 */
export function hybridEncrypt(content, rsaPublicKey) {
  if (!content) {
    return content
  }

  if (!JSEncrypt) {
    throw new Error('jsencrypt 未安装，无法使用混合加密')
  }

  if (!CryptoJS) {
    throw new Error('crypto-js 未安装，无法使用混合加密')
  }

  try {
    // 验证 JSEncrypt 是否是构造函数
    if (typeof JSEncrypt !== 'function') {
      throw new Error('JSEncrypt 不是构造函数，类型: ' + typeof JSEncrypt)
    }
    
    // 第一步：生成AES密钥（16字节的随机密钥）
    const aesKeyWordArray = CryptoJS.lib.WordArray.random(16)
    // 转换为Base64字符串用于RSA加密
    const aesKeyBase64 = CryptoJS.enc.Base64.stringify(aesKeyWordArray)
    
    // 第二步：使用AES密钥加密数据（直接使用WordArray）
    const encrypted = CryptoJS.AES.encrypt(
      CryptoJS.enc.Utf8.parse(content),
      aesKeyWordArray,
      {
        mode: CryptoJS.mode.ECB,
        padding: CryptoJS.pad.Pkcs7
      }
    )
    // 转换为 Base64 字符串并处理特殊字符
    let encryptedData = encrypted.ciphertext.toString(CryptoJS.enc.Base64)
    encryptedData = encryptedData.replace(/\//g, '~SLASH~').replace(/\+/g, '~SUB~')
    
    // 第三步：使用RSA公钥加密AES密钥（Base64字符串）
    const encryptedKey = rsaEncrypt(aesKeyBase64, rsaPublicKey)
    
    if (!encryptedKey) {
      throw new Error('RSA加密AES密钥失败，返回null')
    }
    
    // 第四步：组装JSON格式的加密数据
    const result = {
      encryptedKey: encryptedKey,
      encryptedData: encryptedData
    }
    
    const finalResult = JSON.stringify(result)
    return finalResult
  } catch (error) {
    throw new Error('混合加密失败: ' + error.message)
  }
}

/**
 * 混合解密：使用RSA私钥解密AES密钥，使用AES密钥解密数据
 * 数据格式：{"encryptedKey":"RSA加密的AES密钥","encryptedData":"AES加密的数据"}
 * 
 * @param {String} encryptedContent - 加密内容（JSON字符串或对象）
 * @param {String} rsaPrivateKey - RSA私钥（Base64编码）
 * @returns {String} 解密后的原始数据
 */
export function hybridDecrypt(encryptedContent, rsaPrivateKey) {
  if (!encryptedContent) {
    return encryptedContent
  }

  if (!JSEncrypt) {
    throw new Error('jsencrypt 未安装，无法使用混合解密')
  }

  if (!CryptoJS) {
    throw new Error('crypto-js 未安装，无法使用混合解密')
  }

  try {
    // 第一步：解析加密数据（可能是JSON字符串或对象）
    let encryptedObj
    if (typeof encryptedContent === 'string') {
      encryptedObj = JSON.parse(encryptedContent)
    } else {
      encryptedObj = encryptedContent
    }

    // 验证加密对象结构
    if (!encryptedObj || !encryptedObj.encryptedKey || !encryptedObj.encryptedData) {
      throw new Error('加密数据格式错误，缺少 encryptedKey 或 encryptedData')
    }

    // 第二步：使用RSA私钥解密AES密钥（得到Base64字符串）
    const aesKeyBase64 = rsaDecrypt(encryptedObj.encryptedKey, rsaPrivateKey)
    
    // 验证AES密钥Base64格式是否正确
    if (aesKeyBase64) {
      const base64Regex = /^[A-Za-z0-9+/=]+$/
      if (!base64Regex.test(aesKeyBase64)) {
        throw new Error('AES密钥Base64格式不正确')
      }
    }
    
    // 验证解密结果
    if (!aesKeyBase64 || typeof aesKeyBase64 !== 'string') {
      throw new Error('RSA解密AES密钥失败，返回值为: ' + typeof aesKeyBase64 + ', 值: ' + aesKeyBase64)
    }
    
    // 验证AES密钥Base64长度（16字节的密钥Base64编码后应该是24个字符，但可能有填充）
    const expectedLength = 24 // Base64编码的16字节密钥应该是24个字符（无填充）或22-24字符（可能有填充）
    const isValidLength = aesKeyBase64.length >= 20 && aesKeyBase64.length <= 24
    
    if (!isValidLength) {
      throw new Error('AES密钥长度异常，实际长度: ' + aesKeyBase64.length + '，期望长度: ' + expectedLength)
    }
    
    // 第三步：将Base64密钥解码为WordArray用于AES解密
    let aesKeyWordArray
    try {
      // 清理可能的空白字符
      const cleanKey = String(aesKeyBase64).trim()
      aesKeyWordArray = CryptoJS.enc.Base64.parse(cleanKey)
      
      // 验证AES密钥长度（应该是16字节 = 128位）
      if (aesKeyWordArray.sigBytes !== 16) {
        throw new Error('AES密钥长度不正确，期望16字节，实际' + aesKeyWordArray.sigBytes + '字节')
      }
    } catch (parseError) {
      throw new Error('AES密钥Base64解析失败: ' + parseError.message)
    }
    
    // 第四步：恢复特殊字符
    if (typeof encryptedObj.encryptedData !== 'string') {
      throw new Error('encryptedData 不是字符串类型: ' + typeof encryptedObj.encryptedData)
    }
    let encryptedData = String(encryptedObj.encryptedData).replace(/~SLASH~/g, '/').replace(/~SUB~/g, '+')
    
    // 第五步：Base64解码加密数据
    const encryptedDataBytes = CryptoJS.enc.Base64.parse(encryptedData)
    
    // 第六步：创建加密对象
    const encrypted = CryptoJS.lib.CipherParams.create({
      ciphertext: encryptedDataBytes
    })
    
    // 第七步：使用AES密钥解密数据（直接使用WordArray）
    const decrypted = CryptoJS.AES.decrypt(
      encrypted,
      aesKeyWordArray,
      {
        mode: CryptoJS.mode.ECB,
        padding: CryptoJS.pad.Pkcs7
      }
    )
    
    // 检查解密结果是否有效
    if (!decrypted || decrypted.sigBytes === 0) {
      throw new Error('AES解密失败：解密结果为空或无效')
    }
    
    // 第八步：转换为UTF-8字符串
    let decryptedData
    try {
      // 尝试转换为UTF-8
      decryptedData = decrypted.toString(CryptoJS.enc.Utf8)
    } catch (utf8Error) {
      // 如果UTF-8转换失败，说明AES密钥可能不正确
      throw new Error('AES解密失败：解密后的数据不是有效的UTF-8格式。这可能是因为RSA解密AES密钥失败，导致使用了错误的AES密钥。请检查RSA私钥是否正确配置。错误详情: ' + utf8Error.message)
    }
    
    // 验证解密后的数据是否为空
    if (!decryptedData || decryptedData.length === 0) {
      throw new Error('AES解密后数据为空')
    }
    
    return decryptedData
  } catch (error) {
    throw new Error('混合解密失败: ' + error.message)
  }
}

export default {
  CryptoType,
  aesEncrypt,
  aesDecrypt,
  rsaEncrypt,
  rsaDecrypt,
  encrypt,
  decrypt,
  generateAESKey,
  hybridEncrypt,
  hybridDecrypt
}

